import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Calculator, Users, LogOut, ExternalLink, Factory, Calendar, Clock, Package, Bot, Truck, UserCheck, Scissors, ListTodo, ClipboardList, FileText, Mail, Ruler, Camera, User, Settings, DollarSign, BookOpen, ShoppingCart, Sparkles, UserCog, Shield, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { format, addWeeks } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import MobileModeToggle from "@/components/mobile/MobileModeToggle";
import MobileBottomNav from "@/components/mobile/MobileBottomNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [todayTurnaround, setTodayTurnaround] = useState(4); // Default 4 weeks
  const [recentShipments, setRecentShipments] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const navigate = useNavigate();

  // Check if already logged in
  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    const storedUser = sessionStorage.getItem("adminUser");
    if (adminAuth === "true" && storedUser) {
      setIsAuthenticated(true);
      setCurrentUser(JSON.parse(storedUser));
      fetchTodayTurnaround();
      fetchRecentShipments();
      fetchTodayTasks();
      fetchUnreadMessages(JSON.parse(storedUser));
    }
  }, []);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Get Qingdao, China time (UTC+8)
  const getFactoryTime = () => {
    return currentTime.toLocaleString('en-US', {
      timeZone: 'Asia/Shanghai',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  // Get local time
  const getLocalTime = () => {
    return currentTime.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const fetchTodayTurnaround = async () => {
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const response = await axios.get(`${API}/turnaround-times/${today}`);
      if (response.data?.weeks) {
        setTodayTurnaround(response.data.weeks);
      }
    } catch (error) {
      // Use default 4 weeks
    }
  };

  const fetchRecentShipments = async () => {
    try {
      const response = await axios.get(`${API}/shipping-orders`);
      // Get non-delivered shipments, sorted by estimated arrival
      const active = response.data
        .filter(order => order.status !== "delivered")
        .sort((a, b) => new Date(a.estimated_arrival) - new Date(b.estimated_arrival))
        .slice(0, 3);
      setRecentShipments(active);
    } catch (error) {
      console.error("Failed to fetch shipments:", error);
    }
  };

  const fetchTodayTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks/today`);
      setTodayTasks(response.data.slice(0, 5)); // Show top 5 tasks
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
    }
  };

  const fetchUnreadMessages = async (user) => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/chat/unread`, { params: { user_id: user.id } });
      setUnreadMsgs(res.data?.total || 0);
    } catch {
      setUnreadMsgs(0);
    }
  };

  // Poll unread count every 15s when on dashboard
  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;
    const t = setInterval(() => fetchUnreadMessages(currentUser), 15000);
    return () => clearInterval(t);
  }, [isAuthenticated, currentUser]);

  const estimatedArrival = addWeeks(new Date(), todayTurnaround);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/admin/auth/login`, {
        username,
        password
      });
      
      if (response.data.success) {
        setIsAuthenticated(true);
        setCurrentUser(response.data.user);
        sessionStorage.setItem("adminAuth", "true");
        sessionStorage.setItem("adminUser", JSON.stringify(response.data.user));
        sessionStorage.setItem("adminToken", response.data.token);
        toast.success(`Welcome back, ${response.data.user.full_name}!`);
        fetchTodayTurnaround();
        fetchRecentShipments();
        fetchTodayTasks();
        fetchUnreadMessages(response.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    sessionStorage.removeItem("adminAuth");
    sessionStorage.removeItem("adminUser");
    sessionStorage.removeItem("adminToken");
    toast.info("Logged out successfully");
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full mx-4"
        >
          <div className="bg-card border border-white/10 p-8">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Lock size={32} className="text-primary" />
              </div>
            </div>
            <h1 className="font-serif text-2xl text-center mb-2">Admin Login</h1>
            <p className="text-muted-foreground text-sm text-center mb-6">
              Enter your credentials to access admin tools
            </p>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Username</label>
                <Input
                  type="text"
                  placeholder="Enter username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="bg-background border-white/10"
                  autoComplete="username"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Password</label>
                <Input
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-background border-white/10"
                  autoComplete="current-password"
                />
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <Button
                type="submit"
                className="w-full bg-primary text-primary-foreground hover:bg-foreground"
                disabled={loading || !username || !password}
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </form>
            <p className="text-xs text-muted-foreground text-center mt-4">
              Default: admin / laguerre2025
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="admin-screen min-h-screen pt-20 pb-12">
      {/* Header */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6"
          >
            <div>
              <p className="text-sm text-primary/80 mb-1 tracking-wide">Welcome back</p>
              <h1 className="font-serif text-3xl sm:text-4xl tracking-tight">
                {currentUser?.full_name || "Admin"}
              </h1>
              {currentUser && (
                <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
                  <span className="font-mono text-xs bg-white/5 px-2 py-1 rounded">{currentUser.salesperson_id}</span>
                  {currentUser.role === "admin" && <span className="px-2 py-0.5 bg-primary/20 text-primary rounded text-xs font-medium">Admin</span>}
                </p>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <MobileModeToggle />
              {/* Time Display */}
              <div className="bg-card/50 backdrop-blur-sm border border-white/5 px-5 py-3 rounded-sm">
                <div className="flex gap-6">
                  <div>
                    <p className="text-[10px] text-muted-foreground font-medium tracking-wider mb-1">LOCAL</p>
                    <p className="text-sm font-mono text-foreground">{getLocalTime()}</p>
                  </div>
                  <div className="border-l border-white/10 pl-6">
                    <p className="text-[10px] text-amber-500/80 font-medium tracking-wider mb-1">FACTORY</p>
                    <p className="text-sm font-mono text-amber-400">{getFactoryTime()}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                {currentUser?.role === "admin" && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate("/admin/users")}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid="users-btn"
                    title="Manage Users"
                  >
                    <Settings size={18} />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={() => navigate("/admin/settings")}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="account-settings-btn"
                  title="Account Settings"
                >
                  <UserCog size={18} />
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="logout-btn"
                  title="Logout"
                >
                  <LogOut size={18} />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Actions Bar */}
      <section className="py-6 border-b border-white/5 bg-card/30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => navigate("/admin/pos")}
              className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(212,175,55,0.15)] font-medium"
              data-testid="quick-new-order"
            >
              <ShoppingCart size={18} className="mr-2" />
              New Order
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/crm")}
              className="border-white/10 hover:border-primary/50 hover:text-primary"
              data-testid="quick-clients"
            >
              <Users size={18} className="mr-2" />
              Clients
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/invoices")}
              className="border-white/10 hover:border-primary/50 hover:text-primary"
              data-testid="quick-invoices"
            >
              <FileText size={18} className="mr-2" />
              Invoices
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/calendar")}
              className="border-white/10 hover:border-primary/50 hover:text-primary"
              data-testid="quick-calendar"
            >
              <Calendar size={18} className="mr-2" />
              Calendar
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/quick-order")}
              className="border-white/10 hover:border-primary/50 hover:text-primary"
              data-testid="quick-order-header"
            >
              <FileText size={18} className="mr-2" />
              Quick Order
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/messages")}
              className="border-white/10 hover:border-primary/50 hover:text-primary relative"
              data-testid="quick-messages"
            >
              <MessageSquare size={18} className="mr-2" />
              Messages
              {unreadMsgs > 0 && (
                <span className="ml-2 bg-red-500 text-white text-[10px] font-medium rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid="quick-messages-unread">
                  {unreadMsgs}
                </span>
              )}
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Overview */}
      <section className="py-8">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Turnaround */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card/40 backdrop-blur-sm border border-white/5 p-5 rounded-sm hover:border-white/10 transition-all"
            >
              <div className="flex items-center gap-2 mb-3">
                <Clock size={16} className="text-primary" />
                <span className="text-[10px] text-muted-foreground tracking-wider font-medium">TURNAROUND</span>
              </div>
              <p className="font-serif text-3xl text-primary">{todayTurnaround}</p>
              <p className="text-xs text-muted-foreground mt-1">weeks</p>
            </motion.div>

            {/* Estimated Arrival */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-card/40 backdrop-blur-sm border border-white/5 p-5 rounded-sm hover:border-white/10 transition-all"
            >
              <div className="flex items-center gap-2 mb-3">
                <Package size={16} className="text-emerald-400" />
                <span className="text-[10px] text-muted-foreground tracking-wider font-medium">EST. ARRIVAL</span>
              </div>
              <p className="font-serif text-2xl">{format(estimatedArrival, "MMM d")}</p>
              <p className="text-xs text-muted-foreground mt-1">{format(estimatedArrival, "yyyy")}</p>
            </motion.div>

            {/* Active Shipments */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card/40 backdrop-blur-sm border border-white/5 p-5 rounded-sm hover:border-white/10 transition-all cursor-pointer"
              onClick={() => navigate("/admin/shipping")}
            >
              <div className="flex items-center gap-2 mb-3">
                <Truck size={16} className="text-amber-400" />
                <span className="text-[10px] text-muted-foreground tracking-wider font-medium">SHIPMENTS</span>
              </div>
              <p className="font-serif text-3xl">{recentShipments.length}</p>
              <p className="text-xs text-muted-foreground mt-1">in transit</p>
            </motion.div>

            {/* Tasks */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-card/40 backdrop-blur-sm border border-white/5 p-5 rounded-sm hover:border-white/10 transition-all cursor-pointer"
              onClick={() => navigate("/admin/tasks")}
            >
              <div className="flex items-center gap-2 mb-3">
                <ListTodo size={16} className="text-blue-400" />
                <span className="text-[10px] text-muted-foreground tracking-wider font-medium">TODAY'S TASKS</span>
              </div>
              <p className="font-serif text-3xl">{todayTasks.length}</p>
              <p className="text-xs text-muted-foreground mt-1">pending</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Active Shipments */}
      {recentShipments.length > 0 && (
        <section className="py-6">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl">Active Shipments</h2>
              <Button variant="ghost" onClick={() => navigate("/admin/shipping")} className="text-sm text-muted-foreground hover:text-primary">
                View All →
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {recentShipments.map((shipment, index) => (
                <motion.div
                  key={shipment.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="bg-card/40 backdrop-blur-sm border border-white/5 p-4 rounded-sm hover:border-amber-500/30 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-sm">{shipment.garment_type}</h4>
                      <p className="text-xs font-mono text-muted-foreground mt-1">{shipment.tracking_number}</p>
                    </div>
                    <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded font-medium">
                      {formatDate(shipment.estimated_arrival)}
                    </span>
                  </div>
                  <a
                    href={`https://www.ups.com/track?tracknum=${shipment.tracking_number}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-2 bg-[#351c15] text-[#FFB500] hover:bg-[#4a2a1f] transition-colors text-xs font-bold rounded-sm"
                  >
                    <Truck size={14} />
                    Track on UPS
                  </a>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Admin Tools */}
      <section className="py-8">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <h2 className="font-serif text-xl mb-6">Admin Tools</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* POS & Orders - Primary Action */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-primary/10 border border-primary/30 p-5 rounded-sm hover:border-primary/50 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/pos")}
              data-testid="tool-pos"
            >
              <div className="w-10 h-10 rounded-sm bg-primary/20 flex items-center justify-center mb-4 group-hover:bg-primary/30 transition-colors">
                <ShoppingCart size={20} className="text-primary" />
              </div>
              <h3 className="font-medium text-base mb-1">POS & Orders</h3>
              <p className="text-xs text-muted-foreground">
                Create orders • Manage payments
              </p>
            </motion.div>

            {/* Pricing & Fabrics — unified card replacing the separate Price List + Fabric Management entries */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-emerald-500/30 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/pricing-fabrics")}
              data-testid="tool-pricing-fabrics"
            >
              <div className="w-10 h-10 rounded-sm bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                <DollarSign size={20} className="text-emerald-400" />
              </div>
              <h3 className="font-medium text-base mb-1">Pricing &amp; Fabrics</h3>
              <p className="text-xs text-muted-foreground">
                Price tiers • Fabric / button catalog
              </p>
            </motion.div>

            {/* RC Code Book */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.14 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-purple-500/30 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/code-book")}
              data-testid="tool-code-book"
            >
              <div className="w-10 h-10 rounded-sm bg-purple-500/10 flex items-center justify-center mb-4 group-hover:bg-purple-500/20 transition-colors">
                <BookOpen size={20} className="text-purple-400" />
              </div>
              <h3 className="font-medium text-base mb-1">RC Code Book</h3>
              <p className="text-xs text-muted-foreground">
                Fabric catalog • Styles
              </p>
            </motion.div>

            {/* Code Manager */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-amber-500/30 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/code-manager")}
              data-testid="tool-code-manager"
            >
              <div className="w-10 h-10 rounded-sm bg-amber-500/10 flex items-center justify-center mb-4 group-hover:bg-amber-500/20 transition-colors">
                <FileText size={20} className="text-amber-400" />
              </div>
              <h3 className="font-medium text-base mb-1">Code Manager</h3>
              <p className="text-xs text-muted-foreground">
                Rename • Reassign • Add codes
              </p>
            </motion.div>

            {/* RCMTM Portal */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-amber-500/30 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/rcmtm-portal")}
              data-testid="tool-rcmtm-portal"
            >
              <div className="w-10 h-10 rounded-sm bg-amber-500/10 flex items-center justify-center mb-4 group-hover:bg-amber-500/20 transition-colors">
                <Factory size={20} className="text-amber-400" />
              </div>
              <h3 className="font-medium text-base mb-1">RCMTM Portal</h3>
              <p className="text-xs text-muted-foreground">
                Submit factory orders
              </p>
            </motion.div>

            {/* MX MTM Portal */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.16 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-sky-500/30 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/mx-portal")}
              data-testid="tool-mx-portal"
            >
              <div className="w-10 h-10 rounded-sm bg-sky-500/10 flex items-center justify-center mb-4 group-hover:bg-sky-500/20 transition-colors">
                <Factory size={20} className="text-sky-400" />
              </div>
              <h3 className="font-medium text-base mb-1">MX MTM Portal</h3>
              <p className="text-xs text-muted-foreground">
                Wholesale factory orders
              </p>
            </motion.div>

            {/* Order QA / Audit */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.17 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-emerald-500/30 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/order-audit")}
              data-testid="tool-order-audit"
            >
              <div className="w-10 h-10 rounded-sm bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                <Shield size={20} className="text-emerald-400" />
              </div>
              <h3 className="font-medium text-base mb-1">Order QA / Audit</h3>
              <p className="text-xs text-muted-foreground">
                Validate before factory submit
              </p>
            </motion.div>

            {/* Internal Messages */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.19 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-blue-500/30 transition-all cursor-pointer group relative"
              onClick={() => navigate("/admin/messages")}
              data-testid="tool-messages"
            >
              <div className="w-10 h-10 rounded-sm bg-blue-500/10 flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
                <MessageSquare size={20} className="text-blue-400" />
              </div>
              <h3 className="font-medium text-base mb-1 flex items-center gap-2">
                Messages
                {unreadMsgs > 0 && (
                  <span
                    className="bg-red-500 text-white text-[10px] font-medium rounded-full px-2 py-0.5"
                    data-testid="tool-messages-unread"
                  >
                    {unreadMsgs}
                  </span>
                )}
              </h3>
              <p className="text-xs text-muted-foreground">
                Chat with sales & admin team
              </p>
            </motion.div>

            {/* Order Status */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.16 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-amber-500/30 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/orders")}
              data-testid="tool-orders"
            >
              <div className="w-10 h-10 rounded-sm bg-amber-500/10 flex items-center justify-center mb-4 group-hover:bg-amber-500/20 transition-colors">
                <ClipboardList size={20} className="text-amber-400" />
              </div>
              <h3 className="font-medium text-base mb-1">Order Status</h3>
              <p className="text-xs text-muted-foreground">
                Track all orders
              </p>
            </motion.div>

            {/* Invoices */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.18 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-emerald-500/30 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/invoices")}
              data-testid="tool-invoices"
            >
              <div className="w-10 h-10 rounded-sm bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                <FileText size={20} className="text-emerald-400" />
              </div>
              <h3 className="font-medium text-base mb-1">Invoices</h3>
              <p className="text-xs text-muted-foreground">
                Create • Print • Email
              </p>
            </motion.div>

            {/* CRM */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-cyan-500/30 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/crm")}
              data-testid="tool-crm"
            >
              <div className="w-10 h-10 rounded-sm bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 transition-colors">
                <Users size={20} className="text-cyan-400" />
              </div>
              <h3 className="font-medium text-base mb-1">Client CRM</h3>
              <p className="text-xs text-muted-foreground">
                Manage clients • History
              </p>
            </motion.div>

            {/* Alterations */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.22 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-rose-500/30 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/alterations")}
              data-testid="tool-alterations"
            >
              <div className="w-10 h-10 rounded-sm bg-rose-500/10 flex items-center justify-center mb-4 group-hover:bg-rose-500/20 transition-colors">
                <Scissors size={20} className="text-rose-400" />
              </div>
              <h3 className="font-medium text-base mb-1">Alterations</h3>
              <p className="text-xs text-muted-foreground">
                Track alteration orders
              </p>
            </motion.div>

            {/* Calendar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.24 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-indigo-500/30 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/calendar")}
              data-testid="tool-calendar"
            >
              <div className="w-10 h-10 rounded-sm bg-indigo-500/10 flex items-center justify-center mb-4 group-hover:bg-indigo-500/20 transition-colors">
                <Calendar size={20} className="text-indigo-400" />
              </div>
              <h3 className="font-medium text-base mb-1">Calendar</h3>
              <p className="text-xs text-muted-foreground">
                Appointments • Closures
              </p>
            </motion.div>

            {/* Size Charts */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.26 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-white/20 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/size-chart")}
              data-testid="tool-size-chart"
            >
              <div className="w-10 h-10 rounded-sm bg-white/5 flex items-center justify-center mb-4 group-hover:bg-white/10 transition-colors">
                <Ruler size={20} className="text-slate-400" />
              </div>
              <h3 className="font-medium text-base mb-1">Size Charts</h3>
              <p className="text-xs text-muted-foreground">
                Jacket • Pant • Vest • Shirt
              </p>
            </motion.div>

            {/* Shipping */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-orange-500/30 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/shipping")}
              data-testid="tool-shipping"
            >
              <div className="w-10 h-10 rounded-sm bg-orange-500/10 flex items-center justify-center mb-4 group-hover:bg-orange-500/20 transition-colors">
                <Truck size={20} className="text-orange-400" />
              </div>
              <h3 className="font-medium text-base mb-1">Shipping</h3>
              <p className="text-xs text-muted-foreground">
                Track shipments • UPS
              </p>
            </motion.div>

            {/* Tasks */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-teal-500/30 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/tasks")}
              data-testid="tool-tasks"
            >
              <div className="w-10 h-10 rounded-sm bg-teal-500/10 flex items-center justify-center mb-4 group-hover:bg-teal-500/20 transition-colors">
                <ListTodo size={20} className="text-teal-400" />
              </div>
              <h3 className="font-medium text-base mb-1">Tasks</h3>
              <p className="text-xs text-muted-foreground">
                Daily to-do list
              </p>
            </motion.div>

            {/* Pricing Calculator */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32 }}
              className="bg-card/40 border border-white/5 p-5 rounded-sm hover:border-white/20 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/pricing")}
              data-testid="tool-pricing"
            >
              <div className="w-10 h-10 rounded-sm bg-white/5 flex items-center justify-center mb-4 group-hover:bg-white/10 transition-colors">
                <Calculator size={20} className="text-slate-400" />
              </div>
              <h3 className="font-medium text-base mb-1">Cost Calculator</h3>
              <p className="text-xs text-muted-foreground">
                CMT pricing tool
              </p>
            </motion.div>

            {/* AI Tailor */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.34 }}
              className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 p-5 rounded-sm hover:border-purple-500/50 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/ai-tailor")}
              data-testid="tool-ai-tailor"
            >
              <div className="w-10 h-10 rounded-sm bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4 group-hover:from-purple-500/30 group-hover:to-pink-500/30 transition-colors">
                <Sparkles size={20} className="text-purple-400" />
              </div>
              <h3 className="font-medium text-base mb-1">AI Tailor</h3>
              <p className="text-xs text-muted-foreground">
                Style • Posture • Garment analysis
              </p>
            </motion.div>

            {/* Quick Order */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.36 }}
              className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 p-5 rounded-sm hover:border-emerald-500/50 transition-all cursor-pointer group"
              onClick={() => navigate("/admin/quick-order")}
              data-testid="tool-quick-order"
            >
              <div className="w-10 h-10 rounded-sm bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center mb-4 group-hover:from-emerald-500/30 group-hover:to-teal-500/30 transition-colors">
                <FileText size={20} className="text-emerald-400" />
              </div>
              <h3 className="font-medium text-base mb-1">Quick Order</h3>
              <p className="text-xs text-muted-foreground">
                Save measurements & styles
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mobile-only bottom navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default AdminPage;
